import React from "react";
import {
    Col,
    Row,
    Select,
    Form,
    InputNumber,
    Switch,
    Input,
} from "antd";
import { useSelector } from "react-redux";
import { RoundTablePropertiesEnum } from "../../../enums"
import { SEAT_LABEL_OPTIONS, SeatDistribution } from "../../../enums/SeatDistributionEnum"
import { SelectionTypeEnum, SELECTION_OPTIONS } from "../../../enums/SelectionTypeEnum"
import { CategorySelect } from "./CategorySelect";
import { tPropertyBar, tTable } from "../../../assets/libs/i18n/translator";
import { DistributionSelect } from "./DistributionSelect";
import { StartsAtInput } from "./StartsAtInput";

const RoundTablePropertiesPanel = ({ form, getFieldValue, selectedItem: roundTable }) => {
    const { Option } = Select;
    const { Item } = Form;
    const { tableLabeling, seatsQuantity, emptySpaces, maxSeats, maxEmptySpaces, category, selectionType, section: { label }, seatLabeling } = roundTable;
    const categories = useSelector((state) => state.properties.categories);
    const table = roundTable._objects[0]._objects[0];

    // Read the currently selected distribution from the form so the startsAt
    // input reflects the choice immediately, before it is persisted.
    const seatDistribution =
        getFieldValue?.(RoundTablePropertiesEnum.SeatLabelingType) ?? (seatLabeling.key ?? SeatDistribution.NONE);

    return (
        <>
            <Row>
                <Col span={24} className="mb-1">
                    <h3 className="text-center">{tTable("tables")}</h3>
                </Col>
            </Row>
            <Row>
                <Col span={24} className="mb-1">
                    <h3><b>{tPropertyBar("category")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item wrapperCol={{ span: 24 }}>
                        {form("category", {
                            initialValue: category?.key,
                        })(
                            <CategorySelect categories={categories} />
                        )}
                    </Item>
                </Col>
            </Row>
            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tPropertyBar("seats")}</b></h3>
                </Col>

                <Col span={24}>
                    <Item label={tPropertyBar("seatsQuantity")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RoundTablePropertiesEnum.SeatsQuantity, {
                            initialValue: seatsQuantity ?? 0,
                        })(<InputNumber min={1} max={maxSeats} />)}
                    </Item>
                </Col>
                <Col span={24}>
                    <Item label={tTable("emptySpaces")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RoundTablePropertiesEnum.EmptySpacesQuantity, {
                            initialValue: emptySpaces ?? 0,
                        })(<InputNumber disabled={seatsQuantity <= 1} min={0} max={maxEmptySpaces} />)}
                    </Item>
                </Col>
                <Col span={24} className="mb-1">
                    <Item label={tTable("radius")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RoundTablePropertiesEnum.Radius, {
                            initialValue: table.radius ?? 0,
                        })(<InputNumber min={10} />)}
                    </Item>
                </Col>
            </Row>

            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tPropertyBar("sectionLabeling")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item label={tPropertyBar("label")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RoundTablePropertiesEnum.SectionLabel, {
                            initialValue: label ?? "",
                        })(<Input />)}
                    </Item>
                </Col>
            </Row>

            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tPropertyBar("tableLabeling")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item label={tPropertyBar("label")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RoundTablePropertiesEnum.TableLabelingLabel, {
                            initialValue: tableLabeling.label ?? "",
                        })(<Input />)}
                    </Item>
                    <Item label={tPropertyBar("displayedLabel")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RoundTablePropertiesEnum.TableLabelingDisplayedLabel, {
                            initialValue: tableLabeling.displayedLabel ?? "",
                        })(<Input />)}
                    </Item>
                    <Item label={tTable("show")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RoundTablePropertiesEnum.TableLabelingVisible, {
                            valuePropName: "checked",
                            initialValue: tableLabeling.visible ?? false,
                        })(<Switch />)}
                    </Item>
                </Col>
            </Row>

            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tPropertyBar("seatLabeling")}</b></h3>
                </Col>
                <Col span={24} className="mb-1">
                    <Item label={tPropertyBar("distribution")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RoundTablePropertiesEnum.SeatLabelingType, {
                            initialValue: seatLabeling.key ?? SeatDistribution.NONE,
                        })(
                            <DistributionSelect distributionList={SEAT_LABEL_OPTIONS} />
                        )}
                    </Item>
                    {seatDistribution !== SeatDistribution.NONE &&
                        <Item label={tPropertyBar("startsAt")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                            {form(RoundTablePropertiesEnum.SeatLabelingStartsAt, {
                                initialValue: seatLabeling.startsAt ?? 1,
                            })(<StartsAtInput distributionKey={seatDistribution} />)}
                        </Item>
                    }
                </Col>
            </Row>

            <Row>
                <Col span={24} className="mb-1 mt-2">
                    <h3><b>{tTable("miscellaneous")}</b></h3>
                </Col>
                <Col span={24} className="mb-2">
                    <Item label={tTable("selectionType")} labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                        {form(RoundTablePropertiesEnum.SelectionType, {
                            initialValue: selectionType ?? SelectionTypeEnum.bySeat,
                        })(<Select options={SELECTION_OPTIONS}>
                            {SELECTION_OPTIONS.map(({ value, label }) => (
                                <Option key={value} value={value}>
                                    {label}
                                </Option>
                            ))}
                        </Select>)}
                    </Item>
                </Col>
            </Row>
        </>
    );
};

export { RoundTablePropertiesPanel };
